export const DEFAULT_JITTER_BUFFER_CONFIG = {
    targetDelayMs: 150,
    tickRateHz: 22,
    catchupMultiplier: 1.15,
    slowdownMultiplier: 0.85,
    rateSmoothing: 0.04,
    maxBufferMs: 500,
};
export class CombatJitterBuffer {
    config;
    tickMs;
    tickBuckets = new Map();
    playbackTick = 0;
    playbackRate = 1.0;
    highestEnqueuedTick = 0;
    initialized = false;
    totalEnqueued = 0;
    totalDequeued = 0;
    totalDropped = 0;
    constructor(config = {}) {
        this.config = { ...DEFAULT_JITTER_BUFFER_CONFIG, ...config };
        this.tickMs = 1000 / this.config.tickRateHz;
    }
    enqueue(events) {
        if (events.length === 0)
            return;
        for (const event of events) {
            const bucket = this.tickBuckets.get(event.tick);
            if (bucket) {
                bucket.push(event);
            }
            else {
                this.tickBuckets.set(event.tick, [event]);
            }
            if (event.tick > this.highestEnqueuedTick) {
                this.highestEnqueuedTick = event.tick;
            }
        }
        this.totalEnqueued += events.length;
        if (!this.initialized) {
            this.playbackTick = this.highestEnqueuedTick - (this.config.targetDelayMs / this.tickMs);
            this.initialized = true;
        }
    }
    dequeue(dtMs) {
        if (!this.initialized)
            return [];
        // 1. Compute buffer depth
        const depthTicks = this.highestEnqueuedTick - this.playbackTick;
        const depthMs = depthTicks * this.tickMs;
        // 2. Safety valve: drop stale events if buffer exceeds max
        if (depthMs > this.config.maxBufferMs) {
            const snapTick = this.highestEnqueuedTick - (this.config.targetDelayMs / this.tickMs);
            for (const [tick, events] of this.tickBuckets) {
                if (tick < snapTick) {
                    this.totalDropped += events.length;
                    this.tickBuckets.delete(tick);
                }
            }
            this.playbackTick = snapTick;
        }
        // 3. Compute target playback rate
        const currentDepthMs = (this.highestEnqueuedTick - this.playbackTick) * this.tickMs;
        let targetRate;
        if (this.tickBuckets.size === 0) {
            targetRate = 0;
        }
        else if (currentDepthMs > this.config.targetDelayMs * 1.3) {
            targetRate = this.config.catchupMultiplier;
        }
        else if (currentDepthMs < this.config.targetDelayMs * 0.2) {
            targetRate = 0.5;
        }
        else if (currentDepthMs < this.config.targetDelayMs * 0.5) {
            targetRate = this.config.slowdownMultiplier;
        }
        else {
            targetRate = 1.0;
        }
        // 4. Smooth rate transition
        this.playbackRate += (targetRate - this.playbackRate) * this.config.rateSmoothing;
        this.playbackRate = Math.max(0, Math.min(this.config.catchupMultiplier, this.playbackRate));
        // 5. Advance playback clock
        const tickAdvance = (dtMs / 1000) * this.config.tickRateHz * this.playbackRate;
        this.playbackTick += tickAdvance;
        // 6. Release events from tick buckets <= floor(playbackTick)
        const releaseTick = Math.floor(this.playbackTick);
        const released = [];
        const ticksToRelease = [];
        for (const tick of this.tickBuckets.keys()) {
            if (tick <= releaseTick) {
                ticksToRelease.push(tick);
            }
        }
        ticksToRelease.sort((a, b) => a - b);
        for (const tick of ticksToRelease) {
            const events = this.tickBuckets.get(tick);
            released.push(...events);
            this.tickBuckets.delete(tick);
        }
        this.totalDequeued += released.length;
        return released;
    }
    flush() {
        const allTicks = [...this.tickBuckets.keys()].sort((a, b) => a - b);
        const released = [];
        for (const tick of allTicks) {
            released.push(...this.tickBuckets.get(tick));
            this.tickBuckets.delete(tick);
        }
        this.totalDequeued += released.length;
        return released;
    }
    hasEvents() {
        return this.tickBuckets.size > 0;
    }
    getBufferDepthMs() {
        if (!this.initialized)
            return 0;
        return (this.highestEnqueuedTick - this.playbackTick) * this.tickMs;
    }
    getPlaybackRate() {
        return this.playbackRate;
    }
    getStats() {
        return {
            totalEnqueued: this.totalEnqueued,
            totalDequeued: this.totalDequeued,
            totalDropped: this.totalDropped,
            currentBuckets: this.tickBuckets.size,
        };
    }
    reset() {
        this.tickBuckets.clear();
        this.playbackTick = 0;
        this.playbackRate = 1.0;
        this.highestEnqueuedTick = 0;
        this.initialized = false;
        this.totalEnqueued = 0;
        this.totalDequeued = 0;
        this.totalDropped = 0;
    }
}
//# sourceMappingURL=combat-jitter-buffer.js.map